/**
 * Reading the hand-off code out of `/enter?code=…`, and the two rules that go with it.
 *
 * THE URL IS FIXED BY THE CORPUS. "The app opens authors.moonx.app/enter?code=… ... the SPA
 * exchanges the code for a Firebase custom token minted only for the uid carried on that verified
 * ID token and calls signInWithCustomToken" (adr/0007-web-app-stack.md). `/enter` and the
 * parameter name `code` are the only two names in this whole feature that are not ours.
 *
 * RULE ONE — VALIDATE THE LENGTH BEFORE SPENDING A REQUEST. `HandoffExchangeRequest.code` is
 * `minLength: 16, maxLength: 128` (docs/design/07-api/authors-api.yaml). A truncated
 * link — the commonest way this breaks, because chat clients cut URLs — fails that bound, and
 * checking it here turns a confusing 400 into an accurate message without a round trip.
 *
 * RULE TWO — THE CODE IS A CREDENTIAL AND MUST LEAVE THE ADDRESS BAR. See ../navigation.ts.
 */

export const HANDOFF_CODE_PARAM = 'code';

/** From the spec's own bounds. Not a guess and not a round number. */
export const HANDOFF_CODE_MIN_LENGTH = 16;
export const HANDOFF_CODE_MAX_LENGTH = 128;

export type HandoffCodeReading =
  /** No `code` at all. Somebody typed /enter, or followed a link that lost its query. */
  | { readonly kind: 'absent' }
  /** Present but outside the declared bounds — a truncated or mangled link. */
  | { readonly kind: 'malformed'; readonly length: number }
  | { readonly kind: 'present'; readonly code: string };

export function readHandoffCode(search: string): HandoffCodeReading {
  const raw = new URLSearchParams(search).get(HANDOFF_CODE_PARAM);
  if (raw === null) {
    return { kind: 'absent' };
  }

  const code = raw.trim();
  if (code.length === 0) {
    return { kind: 'absent' };
  }
  if (code.length < HANDOFF_CODE_MIN_LENGTH || code.length > HANDOFF_CODE_MAX_LENGTH) {
    return { kind: 'malformed', length: code.length };
  }

  return { kind: 'present', code };
}
